import React, { useState, useEffect } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
import LazyLoad from 'react-lazyload';
// template components
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import InPageLeadForm from '@nmx/template/dist/componentLibrary/modules/InPageLeadForm';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import ExperienceCallout from './components/experienceCallout';
import Faq from './components/faq';
import HeroSection from './components/heroSection';
import InPageLeadFormLazyLoadContainer from '../../components/inPageLeadFormLazyLoadContainer';
import IntroSection from './components/introSection';
import Metadata from './components/metadata';
import ProductCalloutGrouping from './components/productCalloutGrouping/productCalloutGrouping';
import SectionVideoPlayer from './components/sectionVideoPlayer';
import Testimonial from './components/testimonials';
// images for prefetch
import highResDesktopImage from './components/heroSection/images/dip-hero.webp';
import highResMobileImage from './components/heroSection/images/dip-hero-mobile.webp';
// dislaimer footnotes
import {
  DividendsAreNotGuaranteed,
  LoyaltyBasedOnClientData,
} from '../../../utils/disclaimerFootnotes/disclaimerFootnotes';
// other utils
import { getImmediateMatchFeatureFlag } from '../../../utils/getFeatureFlags/getFeatureFlags';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [immediateMatchFeatureFlag, setImmediateMatchFeatureFlag] = useState(false);

  useEffect(() => {
    getImmediateMatchFeatureFlag(setImmediateMatchFeatureFlag, config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      dynamicConfig={dynamicConfig}
      disableDisclaimerTopBorder
      config={config}
      useReCaptcha={loadRecaptcha}
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Insurance',
          linkUrl: '/insurance/',
          linkAriaLabel: 'Northwestern Mutual Insurance',
        },
        {
          pageName: 'Disability Insurance',
          linkUrl: '/disability-insurance/',
          linkAriaLabel: 'Northwestern Mutual Disability Insurance',
        },
        {
          pageName: 'Physician Disability Insurance',
          linkUrl: '/disability-insurance/physicians-and-dentists-disability-insurance/',
          linkAriaLabel: 'Northwestern Mutual Physicians and Dentists Disability Insurance',
        },
      ]}
      customDisclaimers={[
        <>
          <sup>1</sup>&nbsp;Gen Re, U.S. Individual Disability Market Survey, 2021 Results.
        </>,
        <>
          <sup>2</sup>&nbsp;The terms and conditions of your policy will apply.
        </>,
        <>
          {LoyaltyBasedOnClientData(3)}
        </>,
        <>
          <sup>4</sup>&nbsp;Source: S&#38;P Capital IQ Pro. Prepared and calculated by Northwestern Mutual. 2022 U.S. rank based on direct premiums earned. Disability income insurance rank reflects Individual Businesses.
        </>,
        <>
          {DividendsAreNotGuaranteed(5)}
        </>,
        <>
          Northwestern Mutual is the marketing name for The Northwestern Mutual Life Insurance Company, Milwaukee, WI (NM) (life insurance, disability insurance, annuities, and life insurance with long&#45;term care benefits)
        </>,
        <>
          Disability income insurance policies contain some contractual features and optional benefits that may not available in all states. The ability to perform the substantial and material duties of your occupation is only one of the factors that determine eligibility for disability benefits. These policies also contain exclusions, limitations, and reduction of benefit provisions. Eligibility for disability income insurance, additional policy benefits and qualification for benefits is determined on a case&#45;by&#45;case basis. For costs and complete details of coverage, please contact a Northwestern Mutual Financial Professional.
        </>,
        <>
          To be used with forms ICC16.TT.NCDI.(0916), ICC16.TT.GRDI.(0916), ICC16.TT.DOE.(0916), TT.KEY.(0617), TT.BUY.(0617), MN 992&#45;STD, MN 992&#45;LTD, or state equivalent. Not all contracts are available in all states.
        </>,
      ]}
      stateFilingComplianceNumber={<>90&#45;2689&#45;86 (0922)</>}
    >
      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={highResMobileImage} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={highResDesktopImage} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      {/* METADATA */}
      <Metadata image={highResDesktopImage}/>

      {/* HERO */}
      <HeroSection />

      {/* INTRO */}
      <IntroSection />

      {/* VIDEO PLAYER */}
      <SectionVideoPlayer />

      {/* PRODUCT CALLOUT GROUPING */}
      <ProductCalloutGrouping />

      {/* FAQ */}
      <Faq />

      {/* Disability Insurance Experience Callout */}
      <LazyLoad
        offset={200}
        height={300}
        once
        placeholder={
          <section id='physician-disability-insurance-calc-experience-callout'>
            <h2>Disability Insurance Calculator</h2>
            <p>How much disability insurance do you need to protect your income&#63;</p>
          </section>
        }
      >
        <ExperienceCallout />
      </LazyLoad>

      <Testimonial />

      {/* IN-PAGE LEAD FORM SECTION */}
      <InPageLeadFormLazyLoadContainer>
        <InPageLeadForm
          sectionId='in-page-lead' // note: id in-page-lead-form will be on the Form element itself.
          config={config}
          formLocation='physicians-and-dentists-disability-insurance'
          immediateMatchFeatureFlagEnabled={immediateMatchFeatureFlag}
          onFormEngaged={() => {
            if (!loadRecaptcha) {
              setLoadRecaptcha(true);
            }
          }}
          variant='embedded'
        />
      </InPageLeadFormLazyLoadContainer>

      {/* CREDENTIAL CALLOUT GROUPING */}
      <CredentialCalloutGrouping />

      {/* CONTENT HUB */}
      <ContentHub
        sectionId='content-hub'
        backgroundVariant='lightC'
        themeType='lightTheme'
        headingText={<>Boost your knowledge on physician <WidowBlock>disability insurance</WidowBlock></>}
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      {/* CONNECT WITH AN ADVISOR */}
      <AdvisorConnectModule />
    </Page>
  );
};
